import { useEffect, useState, useRef } from "react";
import { Bot, Radio, Power, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";

const eventText = (t, e) => (t.proximityEvents[e.type] || "{name}").replace("{name}", e.other_name || "?");

export const ProximityRadar = ({ t, isLive, user, setUser }) => {
  const [feed, setFeed] = useState({ nearby: [], events: [] });
  const [radarOn, setRadarOn] = useState(user?.radar_enabled !== false);
  const [toggling, setToggling] = useState(false);

  const toggleRadar = async () => {
    setToggling(true);
    try {
      const { data } = await api.post("/proximity/toggle", { enabled: !radarOn });
      setRadarOn(data.radar_enabled);
      if (setUser && user) setUser({ ...user, radar_enabled: data.radar_enabled });
      toast.success(t.radarToggleSaved);
    } catch {
      toast.error("Error");
    } finally {
      setToggling(false);
    }
  };
  const timerRef = useRef(null);

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get("/proximity/feed");
        setFeed(data);
      } catch (e) {}
    };
    poll();
    timerRef.current = setInterval(poll, 6000);
    return () => clearInterval(timerRef.current);
  }, [isLive]);

  return (
    <div data-testid="proximity-radar-section">
      <p className="text-sm text-[#A0A0B0] mb-6">{t.radarSub}</p>

      {/* Radar ON/OFF master switch */}
      <div className={`flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border px-5 py-4 mb-6 ${radarOn ? "border-[#00FFAA]/30 bg-[#00FFAA]/5" : "border-[#FF4444]/35 bg-[#FF4444]/5"}`} data-testid="radar-toggle-card">
        <button
          data-testid="radar-toggle-btn"
          onClick={toggleRadar}
          disabled={toggling}
          className={`inline-flex items-center gap-3 px-6 py-3.5 rounded-full text-xs font-black uppercase tracking-widest transition-all shrink-0 disabled:opacity-60 ${
            radarOn
              ? "bg-[#00FFAA] text-[#05050A] shadow-[0_0_22px_rgba(0,255,170,0.5)]"
              : "bg-[#FF4444] text-white shadow-[0_0_22px_rgba(255,68,68,0.5)]"
          }`}
        >
          <Power size={15} />
          {radarOn ? t.radarToggleOn : t.radarToggleOff}
        </button>
        <p className={`text-[11px] leading-relaxed flex items-start gap-2 ${radarOn ? "text-white/55" : "text-[#FF8888] font-bold"}`} data-testid="radar-toggle-note">
          {!radarOn && <EyeOff size={14} className="shrink-0 mt-0.5" />}
          {radarOn ? t.radarVisibleNote : t.radarInvisibleNote}
        </p>
      </div>

      {!isLive && (
        <p className="text-xs text-[#00F5FF] bg-[#00F5FF]/5 border border-[#00F5FF]/25 rounded-xl px-4 py-3 mb-6 inline-block" data-testid="radar-golive-hint">
          {t.goLiveFirst}
        </p>
      )}

      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 transition-opacity ${!radarOn ? "opacity-30 pointer-events-none grayscale" : ""}`} data-testid="radar-content">
        <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-7 flex flex-col items-center">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-6 flex items-center gap-2">
            <Radio size={13} className="text-[#00FFAA]" /> {t.radarTitle}
          </p>
          <div className="relative w-64 h-64" data-testid="radar-animation">
            {[1, 0.72, 0.44].map((s, i) => (
              <div key={i} className="absolute rounded-full border border-[#00FFAA]/20"
                style={{ inset: `${(1 - s) * 50}%` }} />
            ))}
            <div className={`absolute inset-0 rounded-full overflow-hidden ${isLive ? "" : "opacity-30"}`}>
              <div className="absolute inset-0 animate-radar-sweep"
                style={{ background: "conic-gradient(from 0deg, rgba(0,255,170,0.35), transparent 70deg)" , borderRadius: "9999px"}} />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`w-3 h-3 rounded-full bg-[#00FFAA] ${isLive ? "animate-ping" : "opacity-30"}`} />
            </div>
            {feed.nearby.slice(0, 6).map((n, i) => {
              const angle = (i * 137) % 360;
              const r = n.in_range ? 22 : 40;
              const x = 50 + r * Math.cos((angle * Math.PI) / 180);
              const y = 50 + r * Math.sin((angle * Math.PI) / 180);
              return (
                <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
                  style={{ left: `${x}%`, top: `${y}%` }} data-testid="radar-blip">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center border ${
                    n.in_range ? "bg-[#00FFAA]/20 border-[#00FFAA]/60" : "bg-[#B026FF]/20 border-[#B026FF]/50"
                  }`}>
                    <Bot size={14} className={n.in_range ? "text-[#00FFAA]" : "text-[#D080FF]"} />
                  </span>
                  <span className="text-[8px] text-white/50 font-bold mt-1 max-w-[70px] truncate">{n.avatar_name}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-6 w-full space-y-2">
            {feed.nearby.slice(0, 5).map((n, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                <span className="text-white/70 font-bold truncate">{n.avatar_name}</span>
                {n.in_range ? (
                  <span className="text-[9px] font-bold tracking-widest text-[#00FFAA]">{t.inRange} {n.distance != null && `· ${n.distance}m`}</span>
                ) : (
                  <span className="text-[9px] text-white/30">{n.distance != null ? `${n.distance >= 1000 ? (n.distance / 1000).toFixed(1) + " km" : n.distance + " m"} ${t.away}` : "—"}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-7">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-5">{t.logTitle}</p>
          {feed.events.length === 0 && <p className="text-xs text-white/30">{t.noEvents}</p>}
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="proximity-event-log">
            {feed.events.map((e) => (
              <div key={e._id} className={`text-xs p-3 rounded-xl border leading-relaxed ${
                e.type === "match_created"
                  ? "bg-[#00FFAA]/8 border-[#00FFAA]/30 text-[#00FFAA]"
                  : "bg-white/5 border-white/10 text-white/70"
              }`} data-testid="proximity-event-row">
                {eventText(t, e)}
                <span className="block text-[9px] text-white/25 mt-1">{new Date(e.created_at).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
